import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Box,
  Avatar,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import api, { API_BASE_URL } from "../../utils/api";

const GateVisitorDetailsModal = ({
  visitor,
  open,
  onClose,
  onSave,
  onChange,
  onUploadImage,
  visitorTypes = [],
  visitorStatuses = [],
  onAfterSave, 
}) => {
  const [preview, setPreview] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setImageError(false);
    }
  }, [open]);

  if (!visitor) return null;

  const initials = `${visitor.FirstName?.[0] || ""}${visitor.LastName?.[0] || ""}`.toUpperCase();
  const photoUrl = visitor.PicturePath
    ? `${API_BASE_URL}/gate-visitor-images/${visitor.PicturePath}`
    : null;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreview(URL.createObjectURL(file));

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("visitId", visitor.VisitID);

        const res = await api.post("/upload-gate-visitor-image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data?.filename) {
          onChange("PicturePath", res.data.filename);
        }
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Edit Gate Visitor #{visitor.VisitID}
        <IconButton edge="end" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Avatar
            src={preview || (!imageError ? photoUrl : undefined)}
            sx={{ width: 120, height: 120, fontSize: 40 }}
            onError={() => setImageError(true)}
          >
            {initials}
          </Avatar>
        </Box>

        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Button variant="outlined" component="label" startIcon={<PhotoCamera />}>
            Upload New Photo
            <input type="file" accept="image/*" hidden onChange={handleFileChange} />
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Visitor First Name"
              fullWidth
              value={visitor.FirstName || ""}
              onChange={(e) => onChange("FirstName", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Visitor Last Name"
              fullWidth
              value={visitor.LastName || ""}
              onChange={(e) => onChange("LastName", e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Arrival Date"
              type="datetime-local"
              fullWidth
              value={visitor.ArrivalDate ? visitor.ArrivalDate.slice(0, 16) : ""}
              onChange={(e) => onChange("ArrivalDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Departure Date"
              type="datetime-local"
              fullWidth
              value={visitor.DepartureDate ? visitor.DepartureDate.slice(0, 16) : ""}
              onChange={(e) => onChange("DepartureDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={visitor.Description || ""}
              onChange={(e) => onChange("Description", e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Vehicle Plate"
              fullWidth
              value={visitor.VehiclePlate || ""}
              onChange={(e) => onChange("VehiclePlate", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Vehicle Type"
              fullWidth
              value={visitor.VehicleType || ""}
              onChange={(e) => onChange("VehicleType", e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Visitor Type"
              select
              fullWidth
              value={visitor.VisitorTypeID || ""}
              onChange={(e) => onChange("VisitorTypeID", Number(e.target.value))}
            >
              {visitorTypes.map((type) => (
                <MenuItem key={type.TypeID} value={type.TypeID}>
                  {type.TypeName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Status"
              select
              fullWidth
              value={visitor.StatusID || ""}
              onChange={(e) => onChange("StatusID", Number(e.target.value))}
            >
              {visitorStatuses.map((status) => (
                <MenuItem key={status.StatusID} value={status.StatusID}>
                  {status.StatusName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes"
              multiline
              rows={3}
              fullWidth
              value={visitor.Notes || ""}
              onChange={(e) => onChange("Notes", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
  onClick={async () => {
    await onSave();
    if (typeof onAfterSave === "function") {
      onAfterSave(); // ← reload visitors after save
    }
  }}
  variant="contained"
  color="primary"
>
  Save Changes
</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GateVisitorDetailsModal;
